import React from 'react';
import {StyleSheet, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import {FontSizes, isPhone} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useArenaStore from '@/Store/ArenaStore';
import {
  lbColors,
  lbRadius,
  lbShadow,
} from '@/Screens/Leaderboard/leaderboardTheme';
import Spacer from '@/Atoms/Spacer';
import {vs} from '@/Utils/Dimensions';
import ArenaSectionHeader from './ArenaSectionHeader';

// ── ArenaDetails ─────────────────────────────────────────────────────────────
// Arena-specific replacement for TournamentDetails (the Swiss details view),
// themed to the wins-leaderboard look (parchment ground, white cards, gold
// accents). Surfaces arena-relevant facts: continuous re-pairing format, the
// fixed duration_minutes clock (the Swiss view reads the wrong field for
// arenas), the arena scoring/fire/berserk rules, and a plain-language
// "how it works".

dayjs.extend(utc);
dayjs.extend(timezone);

// Bullet/Blitz/Rapid from the base-minutes half of a "M+I" time control.
export function deriveSpeedLabel(timeControl?: string): string {
  const base = parseInt(String(timeControl || '').split('+')[0], 10);
  if (!base || isNaN(base)) {
    return '';
  }
  if (base < 3) {
    return 'Bullet';
  }
  if (base < 10) {
    return 'Blitz';
  }
  return 'Rapid';
}

function formatDateTime(input?: string): string {
  if (!input) {
    return '';
  }
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return dayjs.utc(input).tz(userTimeZone).format('MMM D, YYYY, hh:mm A');
}

interface ArenaDetailsData {
  time_control?: string;
  duration_minutes?: number;
  players_count?: number;
  organizer?: string;
  start_date?: string;
  end_date?: string;
  berserkEnabled?: boolean;
}

// Each metric gets its own accent (a left stripe + a tinted icon chip) so the
// grid reads as a set of distinct facts rather than identical white boxes.
const InfoCard: React.FC<{
  icon: string;
  label: string;
  value: string;
  accent: string;
  accentBg: string;
}> = ({icon, label, value, accent, accentBg}) => (
  <View style={[styles.card, isPhone && {width: '100%'}]}>
    <View style={[styles.accentBar, {backgroundColor: accent}]} />
    <View style={[styles.icoChip, {backgroundColor: accentBg}]}>
      <MaterialCommunityIcons name={icon} size={17} color={accent} />
    </View>
    <View style={{flex: 1, minWidth: 0}}>
      <CCText fontSize={FontSizes[11]} style={styles.cardLabel}>
        {label}
      </CCText>
      <CCText
        fontSize={FontSizes[14]}
        style={styles.cardValue}
        numberOfLines={2}>
        {value}
      </CCText>
    </View>
  </View>
);

// A titled card with a tinted header band (icon + colored bullet dots) so each
// rule group has its own identity within the parchment theme.
const RuleSection: React.FC<{
  title: string;
  icon: string;
  rules: string[];
  headBg: string;
  headInk: string;
  dot: string;
}> = ({title, icon, rules, headBg, headInk, dot}) => (
  <View style={styles.section}>
    <Box
      flexDirection="row"
      alignItems="center"
      gap={9}
      style={[styles.ruleHead, {backgroundColor: headBg}]}>
      <View style={styles.rico}>
        <MaterialCommunityIcons name={icon} size={14} color={headInk} />
      </View>
      <CCText
        fontSize={FontSizes[14]}
        fontWeight="bold"
        style={{color: headInk}}>
        {title}
      </CCText>
    </Box>
    <View style={styles.ruleBody}>
      {rules.map((rule, i) => (
        <Box key={i} flexDirection="row" style={styles.ruleItem} gap={8}>
          <View style={[styles.bulletDot, {backgroundColor: dot}]} />
          <CCText
            fontSize={FontSizes[13]}
            style={{color: lbColors.inkSoft, flex: 1}}>
            {rule}
          </CCText>
        </Box>
      ))}
    </View>
  </View>
);

const ArenaDetails: React.FC<{
  tournamentId: number | string;
  data?: ArenaDetailsData;
}> = ({data}) => {
  // Berserk truth: prefer the live arena status flag, fall back to details.
  const berserkFromStore = useArenaStore(state => state.status?.berserkEnabled);
  const berserkEnabled = berserkFromStore ?? data?.berserkEnabled ?? true;

  // Duration is the one fact the TournamentPlay header doesn't already show
  // (Format/Time control/Players are covered by the "⚡ Arena • …" header
  // strip and the "How it works" rule card below).
  const infoCards = [
    {
      icon: 'clock-outline',
      label: 'Duration',
      value: data?.duration_minutes ? `${data.duration_minutes} min` : 'N/A',
      accent: lbColors.orange,
      accentBg: '#FCEBE1',
    },
  ];

  if (data?.organizer) {
    infoCards.push({
      icon: 'account-tie-outline',
      label: 'Organizer',
      value: String(data.organizer),
      accent: lbColors.green,
      accentBg: lbColors.greenBg,
    });
  }
  const startStr = formatDateTime(data?.start_date);
  if (startStr) {
    infoCards.push({
      icon: 'calendar-outline',
      label: 'Starts',
      value: startStr,
      accent: '#47A6D6',
      accentBg: '#E4F1F9',
    });
  }
  const endStr = formatDateTime(data?.end_date);
  if (endStr) {
    infoCards.push({
      icon: 'calendar-check-outline',
      label: 'Ends',
      value: endStr,
      accent: lbColors.red,
      accentBg: '#FBE7DF',
    });
  }

  const scoringRules = [
    'Win 2 · Draw 1 · Loss 0.',
    'Win 2 games in a row and your results DOUBLE (on fire) until you don’t win.',
    'Berserk a game and win it (≥7 of your moves) for +1.',
  ];

  return (
    <View style={styles.container}>
      <ArenaSectionHeader
        title="Details"
        subtitle="Format, scoring and berserk rules"
      />
      <Spacer spacing={vs(10)} />

      <View style={[styles.infoGrid, isPhone && {flexDirection: 'column'}]}>
        {infoCards.map((item, idx) => (
          <InfoCard
            key={idx}
            icon={item.icon}
            label={item.label}
            value={item.value}
            accent={item.accent}
            accentBg={item.accentBg}
          />
        ))}
      </View>

      <RuleSection
        title="Scoring"
        icon="star-outline"
        rules={scoringRules}
        headBg={lbColors.goldSoft}
        headInk={lbColors.goldInk}
        dot={lbColors.gold}
      />

      <RuleSection
        title="Berserk"
        icon="flash-outline"
        headBg="#FCEBE1"
        headInk="#B54A1E"
        dot={lbColors.orange}
        rules={[
          `${
            berserkEnabled ? 'Enabled' : 'Disabled'
          } — halve your clock before your first move for a bonus point if you win.`,
        ]}
      />

      <RuleSection
        title="How it works"
        icon="information-outline"
        headBg="#E4F5F2"
        headInk="#1E7A6E"
        dot={lbColors.teal}
        rules={[
          'Join the pool and you’re paired automatically. As soon as a game ends you’re re-paired with someone close in the standings. Rack up points before the clock hits zero.',
        ]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {padding: 16, flex: 1, backgroundColor: lbColors.bg},
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  card: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: lbColors.card,
    paddingVertical: 11,
    paddingLeft: 15,
    paddingRight: 12,
    borderRadius: lbRadius.md,
    borderWidth: 1,
    borderColor: lbColors.line,
    marginBottom: 4,
    position: 'relative',
    overflow: 'hidden',
    ...lbShadow.card,
  },
  accentBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  icoChip: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardLabel: {
    fontWeight: '400',
    color: lbColors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  cardValue: {fontWeight: '700', color: lbColors.ink},
  section: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.lg,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: lbColors.line,
    overflow: 'hidden',
    ...lbShadow.card,
  },
  ruleHead: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  rico: {
    width: 24,
    height: 24,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lbColors.card,
  },
  ruleBody: {
    paddingHorizontal: 15,
    paddingTop: 11,
    paddingBottom: 5,
  },
  ruleItem: {marginBottom: 7, alignItems: 'flex-start'},
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: lbColors.gold,
    marginTop: 6,
  },
});

export default ArenaDetails;
